import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { create } from 'zustand';

// ─────────────────────────────────────────────────────────────
// Page Store — lets pages publish a primary action into the TopBar.
// Usage in a page component:
//   usePageAction(<PrimaryButton onClick={…}>…</PrimaryButton>, [deps]);
// The hook clears the slot on unmount so the next page starts clean.
// ─────────────────────────────────────────────────────────────

interface PageStore {
  action: ReactNode | null;
  setAction: (action: ReactNode | null) => void;
}

export const usePageStore = create<PageStore>((set) => ({
  action: null,
  setAction: (action) => set({ action }),
}));

/**
 * Publish a TopBar action button for the current page.
 * Pass `deps` so the action re-registers when underlying handlers change.
 */
export function usePageAction(action: ReactNode, deps: React.DependencyList = []) {
  const setAction = usePageStore((s) => s.setAction);
  useEffect(() => {
    setAction(action);
    return () => setAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAction, ...deps]);
}
